/* global Word, console */
import { stripMarkdown } from "../utils/markdown.utils";
import { sanitizeDraftHtml } from "../utils/html.utils";

export interface CapturedSelection {
  range: Word.Range;
  text: string;
}

/**
 * Capture the current selection text and keep the Range proxy alive across
 * subsequent Word.run calls (the AI round-trip). Text and insert target come
 * from the same getSelection() so they cannot diverge. Insertion must use
 * this range — never getSelection() again after the request returns.
 */
export async function captureSelection(): Promise<CapturedSelection> {
  if (typeof Word === "undefined") {
    throw new Error("Word API not available");
  }
  return Word.run(async (context) => {
    const range = context.document.getSelection();
    range.load("text");
    context.trackedObjects.add(range);
    await context.sync();
    return { range, text: range.text };
  });
}

export async function releaseTrackedRange(range: Word.Range | null): Promise<void> {
  if (range == null || typeof Word === "undefined") {
    return;
  }
  try {
    await Word.run(range, async (context) => {
      context.trackedObjects.remove(range);
      await context.sync();
    });
  } catch {
    // The range may already have been invalidated (user deleted the
    // selection, closed the document, etc.).
  }
}

async function insertHtmlIntoRange(targetRange: Word.Range, html: string): Promise<void> {
  await Word.run(targetRange, async (context) => {
    targetRange.insertHtml(html, Word.InsertLocation.replace);
    await context.sync();
  });
}

async function insertTextIntoRange(targetRange: Word.Range, text: string): Promise<void> {
  await Word.run(targetRange, async (context) => {
    targetRange.insertText(text, Word.InsertLocation.replace);
    await context.sync();
  });
}

/**
 * Replace the captured range with drafted HTML. If insertHtml fails, fall
 * back to plain text on the SAME range. If that range is gone, abort —
 * do not re-query the current selection.
 */
export async function insertContentIntoDocument(
  htmlContent: string,
  targetRange: Word.Range
): Promise<void> {
  if (typeof Word === "undefined") {
    throw new Error("Word API not available");
  }

  const sanitizedHtml = sanitizeDraftHtml(htmlContent);

  try {
    await insertHtmlIntoRange(targetRange, sanitizedHtml);
  } catch (wordError) {
    console.error("[AILA] Failed to insert HTML, falling back to plain text:", wordError);
    try {
      await insertTextIntoRange(targetRange, stripMarkdown(htmlContent));
    } catch (textError) {
      console.error("[AILA] Failed to insert plain text:", textError);
      throw new Error("Failed to insert content into document");
    }
  }
}
